import React, { useState } from 'react';
import { patientAPI } from '../../../lib/api';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { StickyNote, Share2, UserCog, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../lib/utils';

const SPECIALTIES = [
  'General Medicine', 'Cardiology', 'Orthopedics', 'Pediatrics',
  'Gynecology', 'ENT', 'Dermatology', 'Neurology', 'Surgery', 'Emergency'
];

const DEPARTMENTS = ['Laboratory', 'Pharmacy', 'Reception', 'Nursing', 'Emergency'];

const NOTE_TYPES = [
  ['general', 'General Note'], ['observation', 'Patient Observation'], ['vitals', 'Vital Signs'],
  ['progress', 'Progress Note'], ['discharge', 'Discharge Note'],
];

const FormSelect = ({ label, value, onChange, placeholder, children }) => (
  <div>
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent position="popper" sideOffset={5}>{children}</SelectContent>
    </Select>
  </div>
);

const DialogFooter = ({ onCancel, onConfirm, label, className, disabled, variant }) => (
  <div className="flex justify-end gap-2 pt-4">
    <Button variant="outline" onClick={onCancel}>Cancel</Button>
    <Button className={className} variant={variant} onClick={onConfirm} disabled={disabled}>{label}</Button>
  </div>
);

export const AddNoteDialog = ({ patientId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ title: '', note: '', note_type: 'general' });
  const set = (field) => (v) => setData(d => ({ ...d, [field]: v }));

  const submit = async () => {
    try {
      await patientAPI.addNote(patientId, data);
      toast.success('Note added successfully');
      setOpen(false);
      setData({ title: '', note: '', note_type: 'general' });
      onSuccess();
    } catch {
      toast.error('Failed to add note');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-purple-500 hover:bg-purple-600" data-testid="add-note-btn">
          <StickyNote className="w-4 h-4 mr-2" />Add Note
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-[Manrope]">Add Note/Observation</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <FormSelect label="Note Type" value={data.note_type} onChange={set('note_type')}>
            {NOTE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </FormSelect>
          <div>
            <Label>Title</Label>
            <Input value={data.title} onChange={(e) => set('title')(e.target.value)} placeholder="e.g., Morning Rounds, Vitals Check" />
          </div>
          <div>
            <Label>Note / Observation</Label>
            <Textarea value={data.note} onChange={(e) => set('note')(e.target.value)} placeholder="Enter your notes or observations here..." rows={5} />
          </div>
          <DialogFooter onCancel={() => setOpen(false)} onConfirm={submit} label="Add Note" className="bg-purple-500 hover:bg-purple-600" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ReferPatientDialog = ({ patientId, doctors, excludeDoctorId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ refer_to_type: 'doctor', refer_to_id: '', reason: '', urgency: 'normal' });
  const set = (field) => (v) => setData(d => ({ ...d, [field]: v }));

  const submit = async () => {
    try {
      await patientAPI.refer(patientId, data);
      toast.success('Patient referred successfully');
      setOpen(false);
      setData({ refer_to_type: 'doctor', refer_to_id: '', reason: '', urgency: 'normal' });
      onSuccess();
    } catch {
      toast.error('Failed to refer patient');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-teal-500 hover:bg-teal-600" data-testid="refer-patient-btn">
          <Share2 className="w-4 h-4 mr-2" />Refer Patient
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-[Manrope]">Refer Patient</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <FormSelect label="Refer To" value={data.refer_to_type} onChange={(v) => setData(d => ({ ...d, refer_to_type: v, refer_to_id: '' }))}>
            <SelectItem value="doctor">Another Doctor/Specialist</SelectItem>
            <SelectItem value="department">Department</SelectItem>
          </FormSelect>

          {data.refer_to_type === 'doctor' ? (
            <FormSelect label="Select Doctor" value={data.refer_to_id} onChange={set('refer_to_id')} placeholder="Select doctor">
              {doctors.filter(d => d.id !== excludeDoctorId).map(doc => (
                <SelectItem key={doc.id} value={doc.id}>Dr. {doc.full_name} {doc.department ? `- ${doc.department}` : ''}</SelectItem>
              ))}
            </FormSelect>
          ) : (
            <FormSelect label="Select Department" value={data.refer_to_id} onChange={set('refer_to_id')} placeholder="Select department">
              {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </FormSelect>
          )}

          <FormSelect label="Urgency" value={data.urgency} onChange={set('urgency')}>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </FormSelect>

          <div>
            <Label>Reason for Referral *</Label>
            <Textarea value={data.reason} onChange={(e) => set('reason')(e.target.value)} placeholder="Why are you referring this patient?" rows={4} required />
          </div>

          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              The referred {data.refer_to_type === 'doctor' ? 'doctor' : 'department'} will be notified and can view this patient.
            </p>
          </div>

          <DialogFooter onCancel={() => setOpen(false)} onConfirm={submit} label="Send Referral" className="bg-teal-500 hover:bg-teal-600" disabled={!data.refer_to_id || !data.reason} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AssignPatientDialog = ({ patient, doctors, nurses, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({
    specialty: patient.specialty || '',
    assigned_doctor_id: patient.assigned_doctor_id || '',
    assigned_nurse_id: patient.assigned_nurse_id || '',
  });
  const set = (field) => (v) => setData(d => ({ ...d, [field]: v }));

  const submit = async () => {
    try {
      await patientAPI.assign(patient.id, data);
      toast.success('Patient assignment updated');
      setOpen(false);
      onSuccess();
    } catch {
      toast.error('Failed to update assignment');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-blue-500 hover:bg-blue-600" data-testid="assign-patient-btn">
          <UserCog className="w-4 h-4 mr-2" />Update Assignment
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-[Manrope]">Assign Patient</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <FormSelect label="Specialty/Department" value={data.specialty} onChange={set('specialty')} placeholder="Select specialty">
            {SPECIALTIES.map(spec => <SelectItem key={spec} value={spec}>{spec}</SelectItem>)}
          </FormSelect>
          <FormSelect label="Assigned Doctor" value={data.assigned_doctor_id} onChange={set('assigned_doctor_id')} placeholder="Select doctor">
            {doctors.filter(d => !data.specialty || d.department === data.specialty).map(doc => (
              <SelectItem key={doc.id} value={doc.id}>Dr. {doc.full_name} {doc.department ? `- ${doc.department}` : ''}</SelectItem>
            ))}
          </FormSelect>
          {patient.patient_type === 'indoor' && (
            <FormSelect label="Assigned Nurse" value={data.assigned_nurse_id} onChange={set('assigned_nurse_id')} placeholder="Select nurse">
              {nurses.map(nurse => <SelectItem key={nurse.id} value={nurse.id}>{nurse.full_name}</SelectItem>)}
            </FormSelect>
          )}
          <DialogFooter onCancel={() => setOpen(false)} onConfirm={submit} label="Update Assignment" className="bg-blue-500 hover:bg-blue-600" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DeletePatientDialog = ({ patientId, isReceptionist, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    try {
      await patientAPI.delete(patientId, reason);
      toast.success('Patient deleted successfully');
      onDeleted();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete patient'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" className="w-full" data-testid="delete-patient-btn">
          <Trash2 className="w-4 h-4 mr-2" />Delete Patient Record
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-[Manrope] text-red-600">Delete Patient Record</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-800"><strong>Warning:</strong> This will permanently mark this patient record as deleted.</p>
          </div>
          <div>
            <Label>Reason for Deletion *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Provide detailed reason for deleting this patient record..." rows={4} required />
          </div>
          {isReceptionist ? (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800"><strong>Note:</strong> You can delete patients within 30 minutes of registration without admin approval.</p>
            </div>
          ) : (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800"><strong>Admin Privilege:</strong> You can delete any patient record with a valid reason.</p>
            </div>
          )}
          <DialogFooter onCancel={() => setOpen(false)} onConfirm={submit} label="Confirm Delete" variant="destructive" disabled={!reason.trim()} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
